/**
 * Favorites Button Component
 * Toggle favorite state for games
 */

import React, { useCallback } from 'react';
import { FavoriteOnIcon, FavoriteOffIcon } from './FavoriteIcon';
import { useGameSounds } from '../../../../../../shared/hooks/useSound';
import styles from '../GameComponents/GameComponents.module.css';
import { useTranslation } from 'react-i18next';
import { clickCounter } from '../../../../../../core/services/EventTracker';

interface ButtonFavoriteProps {
  isFavorite: boolean;
  handleFavoriteClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  gameKey: string;
}

const ButtonFavorites: React.FC<ButtonFavoriteProps> = ({ isFavorite, handleFavoriteClick, gameKey }) => {
  const { playHoverSound } = useGameSounds();
  const { t } = useTranslation();

  // Handle favorites click with hover sound (matches old lobby behavior)
  const handleFavoriteClickWithSound = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Stop event propagation to prevent game card hover sound
    e.stopPropagation();

    // Track with consistent naming: click_favorites_{GAME_KEY}_{add|remove}
    const action = isFavorite ? 'remove' : 'add';
    clickCounter.incrementClick(`click_favorites_${gameKey}_${action}`);

    // Play hover sound when clicking favorites button
    playHoverSound();

    // Call the original handler
    handleFavoriteClick(e);
  }, [playHoverSound, handleFavoriteClick, isFavorite, gameKey]);

  return (
    <div 
      className={`${styles.favoriteButton} ${isFavorite ? styles.isFavorite : styles.notFavorite} egt_game_btn_favorite`}
      onClick={handleFavoriteClickWithSound}
      title={isFavorite ? t('favourites.remove', 'Remove from favourites') : t('favourites.add', 'Add to favourites')}
    >
      {isFavorite ? <FavoriteOnIcon /> : <FavoriteOffIcon />}
    </div>
  );
};

export default ButtonFavorites;